<template>
  <v-container>
    <div class="d-flex">
      <div class="ma-1 anon-playground-controls">
        <v-select
          v-model="bodyId"
          label="Body"
          :items="bodyItems"
        />
        <v-select
          v-model="headId"
          label="Head"
          :items="headItems"
        />
        <v-select
          v-model="hairBackgroundId"
          label="Hair (background)"
          :items="hairBackgroundItems"
        />
        <v-select
          v-model="hairForegroundId"
          label="Hair (foreground)"
          :items="hairForegroundItems"
        />

        <v-radio-group
          v-model="clothingMode"
          label="Clothing"
          inline
        >
          <v-radio
            label="Fullbody"
            value="fullbody"
          />
          <v-radio
            label="Upper/Lowerbody"
            value="split"
          />
        </v-radio-group>

        <v-select
          v-if="clothingMode === 'fullbody'"
          v-model="clothingFullbodyId"
          label="Clothing (fullbody)"
          :items="clothingFullbodyItems"
        />
        <template v-else>
          <v-select
            v-model="clothingUpperbodyId"
            label="Clothing (upperbody)"
            :items="clothingUpperbodyItems"
          />
          <v-select
            v-model="clothingLowerbodyId"
            label="Clothing (lowerbody)"
            :items="clothingLowerbodyItems"
          />
        </template>

        <v-select
          v-model="eyeAreaId"
          label="Eye area"
          :items="eyeAreaItems"
        />
        <v-select
          v-model="headgearId"
          label="Headgear"
          :items="headgearItems"
        />
        <v-select
          v-model="emotion"
          label="Emotion"
          :items="emotionItems"
        />

        <v-text-field
          v-model="name"
          label="Character name (drives hue)"
        />
        <v-slider
          v-model="hueOverride"
          label="Hue override"
          :min="0"
          :max="359"
          :step="1"
          thumb-label
        />
        <div class="text-caption">
          Hue from name: {{ hueFromName }}° - effective hue: {{ hue }}°
        </div>
      </div>
      <div class="ma-1 anon-playground-preview">
        <div
          class="anon-playground-canvas"
          :style="canvasStyle"
        >
          <template
            v-for="(img, idx) in images"
            :key="idx"
          >
            <div
              class="anon-hue-shadow-layer"
              :style="shadowLayerStyle(img)"
            />
            <div
              class="character-generated-layer"
              :style="layerStyle(img, hue)"
            />
          </template>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  ANON_BODY,
  ANON_CLOTHING_FULLBODY,
  ANON_CLOTHING_LOWERBODY,
  ANON_CLOTHING_UPPERBODY,
  ANON_EMOTIONS,
  ANON_EYE_AREA,
  ANON_HAIR_BACKGROUND,
  ANON_HAIR_FOREGROUND,
  ANON_HEAD,
  ANON_HEADGEAR,
  ANON_SPRITE_HEIGHT,
  ANON_SPRITE_WIDTH,
  anonHueShadowLayerStyle,
  anonLayerStyle,
  buildAnonImageStack,
  GeneratedCharacterLook,
  hashHue,
} from '../../../../common/src/Characters'

const NONE = '__none__'

const toItems = (options: { id: string }[], allowNone: boolean) => {
  const items = options.map(o => ({ title: o.id, value: o.id }))
  return allowNone ? [{ title: '(none)', value: NONE }, ...items] : items
}

const bodyItems = toItems(ANON_BODY, false)
const headItems = toItems(ANON_HEAD, false)
const hairBackgroundItems = toItems(ANON_HAIR_BACKGROUND, true)
const hairForegroundItems = toItems(ANON_HAIR_FOREGROUND, false)
const clothingFullbodyItems = toItems(ANON_CLOTHING_FULLBODY, false)
const clothingUpperbodyItems = toItems(ANON_CLOTHING_UPPERBODY, true)
const clothingLowerbodyItems = toItems(ANON_CLOTHING_LOWERBODY, true)
const eyeAreaItems = toItems(ANON_EYE_AREA, false)
const headgearItems = toItems(ANON_HEADGEAR, true)
const emotionItems = ANON_EMOTIONS.map(e => ({ title: e.name, value: e.name }))

const bodyId = ref<string>(ANON_BODY[0].id)
const headId = ref<string>(ANON_HEAD[0].id)
const hairBackgroundId = ref<string>(NONE)
const hairForegroundId = ref<string>(ANON_HAIR_FOREGROUND[0].id)
const clothingMode = ref<'fullbody' | 'split'>('split')
const clothingFullbodyId = ref<string>(ANON_CLOTHING_FULLBODY[0].id)
const clothingUpperbodyId = ref<string>(ANON_CLOTHING_UPPERBODY[0].id)
const clothingLowerbodyId = ref<string>(ANON_CLOTHING_LOWERBODY[0].id)
const eyeAreaId = ref<string>(ANON_EYE_AREA[0].id)
const headgearId = ref<string>(NONE)
const emotion = ref<string>('neutral')
const name = ref<string>('')
const hueOverride = ref<number | undefined>(undefined)

const orNull = (v: string) => (v === NONE ? null : v)

const look = computed<GeneratedCharacterLook>(() => ({
  bodyId: bodyId.value,
  headId: headId.value,
  hairBackgroundId: orNull(hairBackgroundId.value),
  hairForegroundId: hairForegroundId.value,
  clothingFullbodyId: clothingMode.value === 'fullbody' ? clothingFullbodyId.value : null,
  clothingUpperbodyId: clothingMode.value === 'split' ? orNull(clothingUpperbodyId.value) : null,
  clothingLowerbodyId: clothingMode.value === 'split' ? orNull(clothingLowerbodyId.value) : null,
  eyeAreaId: eyeAreaId.value,
  headgearId: orNull(headgearId.value),
}))

const images = computed(() => buildAnonImageStack(look.value, emotion.value))
const hueFromName = computed(() => hashHue(name.value.toLowerCase().trim()))
const hue = computed(() => hueOverride.value ?? hueFromName.value)

const canvasStyle = computed(() => ({
  width: `${ANON_SPRITE_WIDTH}px`,
  height: `${ANON_SPRITE_HEIGHT}px`,
}))

const layerStyle = (image: string, hueValue: number) => anonLayerStyle(`/assets/${image.split('/').map(encodeURIComponent).join('/')}`, hueValue)
const shadowLayerStyle = (image: string) => anonHueShadowLayerStyle(`/assets/${image.split('/').map(encodeURIComponent).join('/')}`, hue.value)
</script>

<style scoped>
.anon-playground-controls {
  width: 320px;
  flex-shrink: 0;
}

.anon-playground-preview {
  flex-grow: 1;
  display: flex;
  justify-content: center;
}

.anon-playground-canvas {
  position: relative;
  background: repeating-conic-gradient(#666 0% 25%, #777 0% 50%) 50% / 20px 20px;
}
</style>
